import { useCallback, useMemo, useReducer } from 'react';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import useAvailabilityCurrentUserActions from './useAvailabilityCurrentUserActions';
import useAvailabilityCurrentUserListeners from './useAvailabilityCurrentUserListeners';
import { StatusType } from '../../../constants';
import { availabilityCurrentUserReducer, initialAvailabilityCurrentUserState } from '../../../reducer';
import { AvailabilityCurrentUserContextValue, CurrentAvailabilityUser } from '../../../types';

type UseAvailabilityCurrentUserControllerParams = Readonly<{
  isAvailabilityEnabled: boolean;
}>;

const useAvailabilityCurrentUserController = ({
  isAvailabilityEnabled,
}: UseAvailabilityCurrentUserControllerParams): AvailabilityCurrentUserContextValue => {
  const [state, dispatch] = useReducer(availabilityCurrentUserReducer, initialAvailabilityCurrentUserState);
  const { isFeatureEnabled } = useFeatureToggle();
  const enabled = isAvailabilityEnabled && isFeatureEnabled(Features.INTERRUPTIONS);
  const { initCurrentUser, setCurrentUserStatus, setStatus } = useAvailabilityCurrentUserActions({
    currentUser: state.user,
    dispatch,
  });

  const handleInitCurrentUser = useCallback(
    (user: CurrentAvailabilityUser) => {
      initCurrentUser(user);

      if (user.status === StatusType.OFFLINE) {
        void setStatus(StatusType.WORKING_INTERRUPTIBLE, user.status);
      }
    },
    [initCurrentUser, setStatus],
  );

  useAvailabilityCurrentUserListeners({
    currentUser: state.user,
    enabled,
    initCurrentUser: handleInitCurrentUser,
    setCurrentUserStatus,
  });

  return useMemo(
    () => ({
      ...state,
      isAvailabilityEnabled,
      setStatus,
    }),
    [isAvailabilityEnabled, setStatus, state],
  );
};

export default useAvailabilityCurrentUserController;
